import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Wrench, Gamepad2, ChevronRight, CheckCircle2, Truck, Store, Check, ArrowRight, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import PageHero from "../components/PageHero";

const REPAIR_IMG = "https://static.prod-images.emergentagent.com/jobs/ab661ab1-b458-474a-b3a9-bee784d2d529/images/aafa7ce3a85f104c3d871c751cb4c0f20854854febe0db264715fd14904d3983.png";

const DEVICE_NAMES = {
  controller: ["PS5 DualSense", "PS4 DualShock 4", "Xbox Controller", "Nintendo Joy-Con", "Nintendo Pro Controller", "Other Controller"],
  console: ["PlayStation 5", "PlayStation 4", "PlayStation 4 Pro", "Nintendo Switch", "Nintendo Switch OLED", "Nintendo Switch Lite", "Xbox Series X", "Xbox Series S", "Other Console"],
};

// Pickup = base price shown. Drop-off adds this surcharge.
const DROPOFF_SURCHARGE = { controller: 150, console: 300 };
const HALL_EFFECT_ADDON = 500; // per eligible stick selected

function fmt(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

export default function RepairsPage() {
  const [options, setOptions] = useState(null);
  const [pricing, setPricing] = useState({});
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    device_type: "",
    device_name: "",
    selected_issues: [], // [{id, label, hall_effect}]
    notes: "",
    pickup_required: true, // default to pickup (cheaper for user)
    address: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [enquiry, setEnquiry] = useState(null);

  useEffect(() => {
    api.get("/repair/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/repair/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function toggleIssue(iss) {
    setForm((f) => {
      const already = f.selected_issues.some((i) => i.id === iss.id);
      return {
        ...f,
        selected_issues: already
          ? f.selected_issues.filter((i) => i.id !== iss.id)
          : [...f.selected_issues, { id: iss.id, label: iss.label, hall_effect: false }],
      };
    });
  }

  function toggleHallEffect(issueId) {
    setForm((f) => ({
      ...f,
      selected_issues: f.selected_issues.map((i) =>
        i.id === issueId ? { ...i, hall_effect: !i.hall_effect } : i
      ),
    }));
  }

  function getIssuePricing(issueId) {
    return pricing[`${form.device_type}_${issueId}`] ?? null;
  }

  // Base estimate from selected issues (pickup price = the listed portal price)
  function calcTotals(pickup) {
    const surcharge = pickup ? 0 : (DROPOFF_SURCHARGE[form.device_type] ?? 0);
    let min = 0, max = 0, hasPricing = false;
    for (const iss of form.selected_issues) {
      const p = getIssuePricing(iss.id);
      if (p) {
        const heAddon = iss.hall_effect ? HALL_EFFECT_ADDON : 0;
        min += (p.min ?? 0) + heAddon;
        max += (p.max ?? 0) + heAddon;
        hasPricing = true;
      }
    }
    if (!hasPricing && form.selected_issues.length === 0) return null;
    return { min: min + surcharge, max: max + surcharge, surcharge, hasPricing };
  }

  const currentIssues = options?.[form.device_type]?.issues ?? [];
  const hallEligibleSelected = form.selected_issues.filter((i) => {
    const opt = currentIssues.find((o) => o.id === i.id);
    return opt?.hall_effect_eligible;
  });

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        device_type: form.device_type,
        device_name: form.device_name,
        issues: form.selected_issues,
        notes: form.notes,
        pickup_required: form.pickup_required,
        address: form.address,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
      };
      const { data } = await api.post("/repair/enquiries", payload);
      setEnquiry(data);
      setStep(3);
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function resetForm() {
    setStep(0);
    setForm({ device_type: "", device_name: "", selected_issues: [], notes: "", pickup_required: true, address: "", customer_name: "", customer_email: "", customer_phone: "" });
    setEnquiry(null);
  }

  return (
    <div>
      <Helmet>
        <title>{"Console & Controller Repair Services — GamesTrade Noida"}</title>
        <meta name="description" content="PS5, PS4, Nintendo Switch, Xbox repair services in Noida. Free diagnostics, genuine parts, 90-day service warranty. Sony Authorised. Call +91 82877 99290." />
        <link rel="canonical" href="https://gamestrade.in/repairs" />
        <meta property="og:title" content="Repair Services — GamesTrade Noida" />
        <meta property="og:description" content="PS5, PS4, Nintendo Switch & Xbox repairs. Free diagnostics, genuine parts, 90-day warranty. Sony Authorised dealer, Noida." />
        <meta property="og:url" content="https://gamestrade.in/repairs" />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageHero
        eyebrow="Service"
        title={<>Repair <span className="text-gold">services</span></>}
        description="25+ years of hands-on expertise. Genuine parts. 90-day service warranty."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Repairs" }]}
      />

      <section className="site-container py-16">
        <div className="mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Book a Repair</div>
          <h2 className="text-3xl font-black uppercase tracking-tighter">Submit an <span className="text-gold">Enquiry</span></h2>
          <p className="text-slate-400 text-sm mt-2">Fill in the form and we'll confirm the final price after diagnosis, then you approve before we proceed.</p>
        </div>

        {step < 3 && (
          <div className="flex items-center gap-2 mb-10">
            {["Device Type", "Issues", "Contact"].map((label, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${i <= step ? "text-gold" : "text-slate-600"}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${i < step ? "bg-gold text-navy-900" : i === step ? "border-2 border-gold text-gold" : "border border-slate-700 text-slate-600"}`}>
                    {i < step ? "✓" : i + 1}
                  </span>
                  <span className="hidden sm:block">{label}</span>
                </div>
                {i < 2 && <ChevronRight className="w-3 h-3 text-slate-700 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step 0: Device type */}
        {step === 0 && (
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { key: "controller", label: "Controller", icon: Gamepad2, desc: "Stick drift, button repair" },
              { key: "console", label: "Console", icon: Wrench, desc: "HDMI, disc drive, power, overheating, servicing" },
            ].map(({ key, label, icon: Icon, desc }) => (
              <button
                key={key}
                onClick={() => { set("device_type", key); setStep(1); }}
                className="bg-navy-700 neon-border rounded-xl p-8 text-left hover:border-gold/60 transition-all group"
              >
                <Icon className="w-10 h-10 text-gold mb-4 group-hover:scale-110 transition-transform" />
                <div className="text-xl font-black uppercase tracking-tight">{label}</div>
                <div className="text-xs text-slate-400 mt-1">{desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* Step 1: Device name + issues + hall effect */}
        {step === 1 && options && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} className="space-y-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-2">Device Name</label>
              <select
                required
                value={form.device_name}
                onChange={(e) => set("device_name", e.target.value)}
                className="w-full bg-navy-700 border border-gold/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-gold/60"
              >
                <option value="">Select device…</option>
                {(DEVICE_NAMES[form.device_type] ?? []).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-gold">
                  Issues <span className="text-slate-500 normal-case font-normal">(select all that apply)</span>
                </label>
                {form.selected_issues.length > 0 && (
                  <span className="text-[10px] font-bold text-gold bg-gold/10 border border-gold/30 rounded-full px-2 py-0.5">
                    {form.selected_issues.length} selected
                  </span>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {currentIssues.map((iss) => {
                  const p = getIssuePricing(iss.id);
                  const selected = form.selected_issues.some((i) => i.id === iss.id);
                  return (
                    <button
                      type="button"
                      key={iss.id}
                      onClick={() => toggleIssue(iss)}
                      className={`text-left rounded-lg p-4 border transition-all flex items-start gap-3 ${selected ? "border-gold bg-gold/10" : "border-gold/20 bg-navy-700 hover:border-gold/40"}`}
                    >
                      <span className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-all ${selected ? "bg-gold border-gold" : "border-slate-600"}`}>
                        {selected && <Check className="w-2.5 h-2.5 text-navy-900 stroke-[3]" />}
                      </span>
                      <div>
                        <div className={`text-sm font-bold ${selected ? "text-gold" : "text-slate-300"}`}>{iss.label}</div>
                        {p && (
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {fmt(p.min)} – {fmt(p.max)}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Hall effect upgrade — shown per eligible selected issue */}
            {hallEligibleSelected.length > 0 && (
              <div className="bg-navy-700/60 border border-gold/20 rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-gold" />
                  <div className="text-xs font-bold uppercase tracking-wider text-gold">Hall Effect Upgrade</div>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  Hall effect sticks use magnetic sensors instead of resistive ones — they don't drift. Recommended for a permanent fix.
                  <span className="text-gold font-bold"> +{fmt(HALL_EFFECT_ADDON)} per stick.</span>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {hallEligibleSelected.map((sel) => {
                    const isOn = sel.hall_effect;
                    return (
                      <button
                        type="button"
                        key={sel.id}
                        onClick={() => toggleHallEffect(sel.id)}
                        className={`text-left rounded-lg p-3 border transition-all ${isOn ? "border-gold bg-gold/10" : "border-gold/20 bg-navy-800 hover:border-gold/40"}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 border transition-all ${isOn ? "bg-gold border-gold" : "border-slate-600"}`}>
                            {isOn && <Check className="w-2 h-2 text-navy-900 stroke-[3]" />}
                          </span>
                          <span className={`text-xs font-bold leading-tight ${isOn ? "text-gold" : "text-slate-300"}`}>
                            {sel.label}
                          </span>
                        </div>
                        <span className={`text-xs font-black ${isOn ? "text-gold" : "text-slate-500"}`}>+{fmt(HALL_EFFECT_ADDON)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Estimate summary + delivery picker — always shown when issues selected */}
            {form.selected_issues.length > 0 && (() => {
              const pickupTotals = calcTotals(true);
              const dropoffTotals = calcTotals(false);
              const sur = DROPOFF_SURCHARGE[form.device_type] ?? 0;
              const hasPricing = pickupTotals?.hasPricing;
              const opts = [
                {
                  v: true,
                  icon: Truck,
                  label: "We pickup from you",
                  sub: "We arrange collection — included in price",
                  price: hasPricing ? `${fmt(pickupTotals.min)} – ${fmt(pickupTotals.max)}` : null,
                  badge: null,
                },
                {
                  v: false,
                  icon: Store,
                  label: "Drop off at store",
                  sub: "Spectrum Mall, Sector 75, Noida",
                  price: hasPricing ? `${fmt(dropoffTotals.min)} – ${fmt(dropoffTotals.max)}` : null,
                  badge: `+${fmt(sur)}`,
                },
              ];
              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-wider text-gold">Estimate & Delivery</div>
                    {!hasPricing && <span className="text-[10px] text-slate-500 italic">Exact quote after diagnosis</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                  {opts.map(({ v, icon: Icon, label, sub, price, badge }) => {
                    const selected = form.pickup_required === v;
                    return (
                      <button
                        type="button"
                        key={String(v)}
                        onClick={() => set("pickup_required", v)}
                        className={`text-left rounded-xl p-4 border transition-all flex flex-col gap-2 ${selected ? "border-gold bg-gold/10" : "border-gold/20 bg-navy-700/60 hover:border-gold/40"}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2 transition-all ${selected ? "border-gold bg-gold/20" : "border-slate-600 bg-navy-800"}`}>
                            <Icon className={`w-3.5 h-3.5 ${selected ? "text-gold" : "text-slate-500"}`} />
                          </div>
                          <div className={`text-xs font-bold leading-tight ${selected ? "text-gold" : "text-slate-300"}`}>
                            {label}
                            {badge && (
                              <span className="ml-1.5 text-[10px] font-bold text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-full px-1.5 py-0.5">{badge}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-[10px] text-slate-500 leading-snug">{sub}</div>
                        <div className="mt-auto">
                          {price
                            ? <span className={`text-sm font-black ${selected ? "text-white" : "text-slate-400"}`}>{price}</span>
                            : <span className="text-[11px] text-slate-500 italic">TBD after diagnosis</span>
                          }
                        </div>
                      </button>
                    );
                  })}
                  </div>
                  <p className="text-[10px] text-slate-500">Estimates are indicative. Final price confirmed after diagnosis — you approve before we proceed.</p>
                </div>
              );
            })()}

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-2">Additional Notes <span className="text-slate-500 normal-case font-normal">(optional)</span></label>
              <textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={3}
                className="w-full bg-navy-700 border border-gold/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-gold/60 resize-none"
                placeholder="Describe the issue in more detail…"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setStep(0)} className="border border-gold/30 text-gold font-bold uppercase text-xs tracking-wider px-6 py-3 rounded-full hover:bg-gold/10 transition">Back</button>
              <button
                type="submit"
                disabled={!form.device_name || form.selected_issues.length === 0}
                className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-8 py-3 rounded-full hover:bg-gold-light transition disabled:opacity-40"
              >
                Continue
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Contact + delivery + address */}
        {step === 2 && (() => {
          const totals = calcTotals(form.pickup_required);
          const sur = DROPOFF_SURCHARGE[form.device_type] ?? 0;
          return (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Summary */}
              <div className="bg-navy-700/50 border border-gold/20 rounded-xl p-4 text-sm space-y-2">
                <div className="text-xs font-bold uppercase tracking-wider text-gold mb-1">Summary</div>
                <p className="text-slate-300 font-bold">{form.device_name}</p>
                <div className="flex flex-wrap gap-1.5">
                  {form.selected_issues.map((i) => (
                    <span key={i.id} className="text-[10px] font-bold uppercase tracking-wider bg-gold/10 text-gold border border-gold/20 rounded-full px-2 py-0.5">
                      {i.label}{i.hall_effect ? " + Hall Effect" : ""}
                    </span>
                  ))}
                </div>
                {(() => {
                  const t = calcTotals(form.pickup_required);
                  const DelivIcon = form.pickup_required ? Truck : Store;
                  return (
                    <div className="flex items-center justify-between pt-2 border-t border-gold/10">
                      <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                        <DelivIcon className="w-3 h-3 text-gold" />
                        {form.pickup_required ? "Pickup by us" : "Self drop-off"}
                      </span>
                      <span className="text-[11px] font-bold text-white">
                        {t?.hasPricing ? `${fmt(t.min)} – ${fmt(t.max)}` : "TBD after diagnosis"}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Contact */}
              {[
                { key: "customer_name", label: "Full Name", type: "text", placeholder: "Your name" },
                { key: "customer_email", label: "Email", type: "email", placeholder: "your@email.com" },
                { key: "customer_phone", label: "Phone / WhatsApp", type: "tel", placeholder: "+91 XXXXX XXXXX" },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-2">{label}</label>
                  <input
                    required
                    type={type}
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    placeholder={placeholder}
                    className="w-full bg-navy-700 border border-gold/20 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-gold/60"
                  />
                </div>
              ))}

              {/* Address — always required */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-2">
                  {form.pickup_required ? "Pickup Address" : "Return Address"}
                  <span className="text-slate-500 normal-case font-normal ml-1">
                    {form.pickup_required ? "(we collect from here)" : "(we ship the repaired device back here)"}
                  </span>
                </label>
                <textarea
                  required
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  rows={2}
                  className="w-full bg-navy-700 border border-gold/20 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-gold/60 resize-none"
                  placeholder="House/flat no., street, area, city, pincode…"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setStep(1)} className="border border-gold/30 text-gold font-bold uppercase text-xs tracking-wider px-6 py-3 rounded-full hover:bg-gold/10 transition">Back</button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-8 py-3 rounded-full hover:bg-gold-light transition disabled:opacity-40"
                >
                  {submitting ? "Submitting…" : "Submit Enquiry"}
                </button>
              </div>
            </form>
          );
        })()}

        {/* Step 3: Success */}
        {step === 3 && enquiry && (
          <div className="text-center py-8">
            <CheckCircle2 className="w-16 h-16 text-gold mx-auto mb-4" />
            <h3 className="text-2xl font-black uppercase tracking-tighter">Enquiry Submitted!</h3>
            <p className="text-slate-400 mt-2 text-sm">Confirmation sent to <strong className="text-white">{form.customer_email}</strong>.</p>
            <div className="mt-4 inline-block bg-navy-700 border border-gold/20 rounded-xl px-6 py-3 text-sm">
              <span className="text-gold font-bold">{enquiry.enquiry_number}</span>
              <span className="text-slate-400 ml-2">— save this reference</span>
            </div>
            <p className="text-slate-400 text-xs mt-6 max-w-sm mx-auto">
              We'll update you at each step. Once we've diagnosed the device, you'll get a quote to approve online before we proceed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link
                to={`/repair/track?ref=${enquiry.enquiry_number}`}
                className="flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-6 py-3 rounded-full hover:bg-gold-light transition"
              >
                Track Repair <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <button onClick={resetForm} className="border border-gold/30 text-gold font-bold uppercase text-xs tracking-wider px-6 py-3 rounded-full hover:bg-gold/10 transition">
                Submit Another
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
